import { classNames } from "@greeter/util";
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ResizeObserver from "resize-observer-polyfill";

import { useGoogleMaps } from "@greeter-venue/contexts/GoogleMapsProvider";

export type Measurement = {
  width: number;
  height: number;
};

export function useMeasure<T extends HTMLElement>(
  listenForResize: boolean = true
): [{ ref: RefObject<T | null> }, Measurement] {
  const ref = useRef<T>(null);
  const [bounds, set] = useState<Measurement>({ width: 0, height: 0 });
  const [observer, setObserver] = useState<ResizeObserver>();

  useEffect(() => {
    if (ref.current && listenForResize) {
      setObserver(
        new ResizeObserver(([entry]: ResizeObserverEntry[]) => {
          set({
            width: entry.contentRect.width + entry.contentRect.left * 2,
            height: entry.contentRect.height + entry.contentRect.top * 2,
          });
        })
      );
    }
  }, [ref.current]);

  useEffect(() => {
    if (observer && ref.current) {
      observer.observe(ref.current);
      return () => observer.disconnect();
    }
  }, [observer, ref.current]);

  return [{ ref }, bounds];
}

export function useClassNames(..._classNames: (string | undefined)[]) {
  return useMemo(() => classNames(..._classNames).className, _classNames);
}

export const useTimeoutManager = () => {
  const [timeoutIds] = useState<number[]>([]);
  const timeoutIdsRef = useRef(timeoutIds);

  useEffect(() => {
    const ref = timeoutIdsRef.current;
    return () => {
      ref.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  const addTimeout = (id: number) => timeoutIdsRef.current.push(id);

  return [
    (handler: TimerHandler, timeout: number | undefined, ...args: any) =>
      addTimeout(window.setTimeout(handler, timeout, args)),
  ];
};

export const useIntervalManager = () => {
  const [intervalIds] = useState<number[]>([]);
  const intervalIdsRef = useRef(intervalIds);

  useEffect(() => {
    const ref = intervalIdsRef.current;
    return () => ref.forEach((id) => window.clearInterval(id));
  }, []);

  const addTimeout = (id: number) => intervalIdsRef.current.push(id);

  return [
    (handler: TimerHandler, timeout: number | undefined, ...args: any) =>
      addTimeout(window.setInterval(handler, timeout, args)),
  ];
};

export type UseGoogleAutocompleteResult = {
  suggestions: string[];
  predictions: google.maps.places.PlacePrediction[];
  details?: google.maps.places.PlaceResult | null;
  getDetails: () => Promise<google.maps.places.PlaceResult | null | undefined>;
};

export type UseGoogleAutocompleteProps = {
  input: string;
  delay?: number;
};

export function useGoogleAutocomplete({
  input,
  delay = 300,
}: UseGoogleAutocompleteProps): UseGoogleAutocompleteResult {
  const { placesLib, map } = useGoogleMaps();
  const sessionToken =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [predictions, setPredictions] = useState<
    google.maps.places.PlacePrediction[]
  >([]);
  const [details, setDetails] =
    useState<google.maps.places.PlaceResult | null>();

  useEffect(() => {
    async function onInput() {
      if (!placesLib) return;

      const { AutocompleteSessionToken, AutocompleteSuggestion } = placesLib;

      if (!sessionToken.current) {
        sessionToken.current = new AutocompleteSessionToken();
      }

      if (input) {
        const suggestions =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            sessionToken: sessionToken.current,
          });
        setSuggestions(
          suggestions.suggestions.map((s) => s.placePrediction?.text.text ?? "")
        );
        setPredictions(
          suggestions.suggestions
            .filter((s) => !!s.placePrediction)
            .map((s) => s.placePrediction!)
        );
      }
    }

    const id = setTimeout(onInput, delay);

    return () => clearTimeout(id);
  }, [input, placesLib]);

  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(
    null
  );

  useEffect(() => {
    console.log("Predictions", { predictions });
    console.log("Suggestions", { suggestions });
    console.log("Details", { details });
  }, [predictions, details]);

  const getDetails = useCallback(async () => {
    if (!placesLib || !map) return;
    if (!placesServiceRef.current) {
      const { PlacesService } = placesLib;
      const s = new PlacesService(map);
      placesServiceRef.current = s;
    }

    return await new Promise<google.maps.places.PlaceResult | null>(
      (resolve, reject) => {
        const s = placesServiceRef.current;
        const p = predictions[0];
        if (p) {
          s?.getDetails(
            {
              placeId: p.placeId ?? "",
              fields: ["address_component"],
              language: "da",
              region: "DK",
              sessionToken: sessionToken.current!,
            },
            (result) => {
              console.log("[getDetails]", { result });
              setDetails(result);
              if (!result) {
                reject();
                return;
              }

              resolve(result);
            }
          );
        }
      }
    );
  }, [placesLib, predictions, map]);

  useEffect(() => void getDetails(), [predictions, suggestions]);

  return {
    suggestions,
    predictions,
    details,
    getDetails,
  };
}
